<template>
  <div class="flex justify-center items-center w-full min-h-screen pb-10">
    <div
      class="max-w-xl w-full sm:rounded-lg sm:shadow-lg px-6 py-10
      flex flex-col items-center justify-center relative"
    >
      <Logo class="place-self-center mb-2" />

      <p class="text-2xl mb-8 h-3">
        {{ resetPasswordMessages.title }}
      </p>

      <div class="flex-col justify-center w-80 space-y-4">
        <PasswordInput
          id="reset-password"
          v-model="password"
          autocomplete="new-password"
          autofocus
          validate-password
          :placeholder="$t('message.password')"
          :is-valid="isValidPassword"
          :aria-label="$t('message.password')"
          aria-required="true"
          @update:isValid="updatePasswordStatus"
        />

        <PasswordInput
          id="reset-confirm-password"
          v-model="confirmPassword"
          autocomplete="new-password"
          :placeholder="$t('message.repeat_password')"
          :error="confirmPasswordError"
          :error-message="confirmPasswordErrorMessage"
          :valid="passwordsMatch && !!confirmPassword"
          :aria-label="$t('message.repeat_password')"
          aria-required="true"
          @blur="markConfirmPasswordTouched"
        />

        <Button
          class="w-80"
          type="primary"
          :disabled="checkPasswordsMatch || !isValidPassword"
          @click="handleResetPassword"
        >
          {{ resetPasswordMessages.btnText }}
        </Button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import PasswordInput from '@/components/atoms/PasswordInput.vue';
import Button from '@/components/atoms/Button.vue';
import Logo from '@/components/atoms/Logo.vue';
import { forgotPasswordSubmit } from '@/service/cognitoService';
import { mapActions, mapGetters } from 'vuex';
import { AppStatus } from '@/model/status';

export default defineComponent({
  name: 'ForgotPasswordConfirm',
  components: {
    PasswordInput,
    Button,
    Logo
  },
  data: () => ({
    password: '',
    confirmPassword: '',
    resetPasswordSuccess: false,
    isValidPassword: false,
    confirmPasswordTouched: false
  }),
  methods: {
    ...mapActions(['setMyEmail', 'setAppStatus', 'startLoading', 'endLoading']),

    async handleResetPassword () {
      try {
        this.startLoading();
        await forgotPasswordSubmit(this.emailToResetPassword, this.codeToResetPassword, this.password);
        this.setAppStatus(AppStatus.ResetPasswordSuccess);
      } catch (error: any) {
        this.setAppStatus(AppStatus.ResetPasswordError);
      } finally {
        this.endLoading();
        this.setMyEmail(this.emailToResetPassword);
        this.pushLogin();
      }
    },
    pushLogin () {
      this.$router.push({
        path: 'login',
        query: {
          client_id: this.$route.query.client_id,
          redirect_uri: this.$route.query.redirect_uri,
          language: this.$route.query.language
        }
      });
    },
    updatePasswordStatus (isValid: boolean) {
      this.isValidPassword = isValid;
    },
    markConfirmPasswordTouched () {
      this.confirmPasswordTouched = true;
    }
  },
  computed: {
    ...mapGetters([
      'global',
      'resetPasswordMessages',
      'emailToResetPassword',
      'codeToResetPassword'
    ]),
    checkPasswordsMatch (): boolean {
      return this.password !== this.confirmPassword;
    },
    confirmPasswordError (): boolean {
      return this.confirmPasswordTouched && this.checkPasswordsMatch;
    },
    confirmPasswordErrorMessage (): string {
      if (!this.confirmPasswordError) {
        return '';
      }

      return this.confirmPassword
        ? this.$t('message.accessibility.passwords_not_match')
        : this.$t('message.accessibility.confirm_password_required');
    },
    passwordsMatch (): boolean {
      return this.password === this.confirmPassword;
    }
  }
});
</script>
